import type { JsonSchema } from "./types";

export function fieldsOf(schema: JsonSchema): Array<[string, JsonSchema]> {
  return Object.entries(schema.properties ?? {});
}

export function isRequired(schema: JsonSchema, key: string): boolean {
  return (schema.required ?? []).includes(key);
}

/**
 * Extract a single-string-literal value from a JSON Schema. Handles both
 * `{ const: "x" }` and `{ enum: ["x"] }` — the latter is how the OpenAPI 3
 * dialect (and zod-to-json-schema's openApi3 target) encodes literals,
 * since OpenAPI 3 has no first-class `const` keyword. Returns null if the
 * schema isn't a single-string literal.
 */
export function literalString(schema: JsonSchema): string | null {
  if (typeof schema.const === "string") return schema.const;
  if (Array.isArray(schema.enum) && schema.enum.length === 1 && typeof schema.enum[0] === "string") {
    return schema.enum[0];
  }
  return null;
}

/**
 * For a JSON Schema with `oneOf`/`anyOf` whose every branch carries a property
 * with a single-string literal value AND that property is marked required in
 * every branch, return the property name. That property is the discriminant
 * we tab on. Returns null if the schema isn't a discriminated union.
 *
 * The `required[]` membership check guards against false positives — e.g. an
 * `anyOf` whose branches happen to differ only in a metadata field's literal
 * value (`version: {enum: ["1"]}` vs `{enum: ["2"]}`) would otherwise be
 * mis-tabbed. Zod's `discriminatedUnion` always puts the discriminant in
 * `required[]`, so this is the correct structural signal.
 */
export function discriminantKey(schema: JsonSchema): string | null {
  const branches = schema.oneOf ?? schema.anyOf;
  if (!branches?.length) return null;
  const candidates = new Map<string, Set<string>>();
  const requiredInEveryBranch = new Map<string, number>();
  for (const branch of branches) {
    for (const [k, v] of Object.entries(branch.properties ?? {})) {
      const lit = literalString(v);
      if (lit !== null) {
        if (!candidates.has(k)) candidates.set(k, new Set());
        candidates.get(k)!.add(lit);
      }
    }
    for (const req of branch.required ?? []) {
      requiredInEveryBranch.set(req, (requiredInEveryBranch.get(req) ?? 0) + 1);
    }
  }
  for (const [k, set] of candidates) {
    if (set.size === branches.length && requiredInEveryBranch.get(k) === branches.length) {
      return k;
    }
  }
  return null;
}

export function activeBranch(
  schema: JsonSchema,
  discriminant: string,
  current: string | undefined,
): JsonSchema | null {
  const branches = schema.oneOf ?? schema.anyOf ?? [];
  if (!branches.length) return null;
  const found = branches.find((b) => {
    const prop = b.properties?.[discriminant];
    return prop ? literalString(prop) === current : false;
  });
  return found ?? branches[0];
}

export function branchLiterals(schema: JsonSchema, discriminant: string): string[] {
  const branches = schema.oneOf ?? schema.anyOf ?? [];
  return branches
    .map((b) => (b.properties?.[discriminant] ? literalString(b.properties[discriminant]) : null))
    .filter((c): c is string => typeof c === "string");
}

export function isPasswordField(name: string, schema: JsonSchema): boolean {
  if (schema.format === "password") return true;
  return /(token|secret|password|apiKey|clientSecret|access_?key)/i.test(name);
}

/**
 * Hide fields whose schema pins them to a single literal value. The operator
 * has no input to give: a `z.literal("v1")` or `z.enum(["only"])` field
 * carries one valid value, set by the schema itself. In practice the only
 * such fields in this codebase are discriminants (`kind`, `mode`, etc.),
 * which the parent SchemaForm renders as tabs.
 *
 * If a future connector needs a fixed-value field that's actually VISIBLE
 * (e.g. for confirmation), model it as `z.string()` with a `.default()`
 * rather than `.literal()`; that surfaces as an editable input pre-filled
 * with the default value.
 */
export function isFieldHidden(schema: JsonSchema): boolean {
  return literalString(schema) !== null;
}

/**
 * Does this secret schema carry any fields to render? Plain-object secrets
 * expose them under `properties`; discriminated-union secrets (e.g. Snowflake
 * `password | key-pair`) expose them under `anyOf`/`oneOf` branches. Returns
 * false for a null or genuinely empty schema (e.g. jump-profile-dest, whose
 * secret is an empty object) so the SECRET section is suppressed only when
 * there's truly nothing to fill in.
 */
export function secretSchemaHasFields(schema: JsonSchema | null): schema is JsonSchema {
  if (!schema) return false;
  const props = (schema as { properties?: Record<string, unknown> }).properties;
  if (props != null && Object.keys(props).length > 0) return true;
  const u = schema as { anyOf?: unknown[]; oneOf?: unknown[] };
  return (Array.isArray(u.anyOf) && u.anyOf.length > 0)
    || (Array.isArray(u.oneOf) && u.oneOf.length > 0);
}
